import React from 'react';
import { HealthDeviceResponseDto } from '../../services/users/types';
import ModalPane from '../UI/ModalPane';
import Tabs, { TabItem } from '../UI/Tabs';
import Button from '../UI/Button';

interface DeviceDetailsViewProps {
  device: HealthDeviceResponseDto;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Component to view device details in a modal with tabs
 */
const DeviceDetailsView: React.FC<DeviceDetailsViewProps> = ({
  device,
  isOpen,
  onClose
}) => {
  // Format booleans for display
  // const formatBoolean = (value: boolean | undefined) => {
  //   if (value === undefined) return '-';
  //   return value ? 'Yes' : 'No';
  // };

  // Format date for display
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Get provider icon
  const getProviderIcon = (provider?: string) => {
    switch (provider) {
      case 'FREESTYLELIBRE':
        return 'fa-heartbeat';
      case 'GARMIN':
        return 'fa-running';
      case 'APPLE':
        return 'fa-apple';
      default:
        return 'fa-mobile-alt';
    }
  };

  // Get provider color classes
  const getProviderColorClasses = (provider?: string) => {
    switch (provider) {
      case 'FREESTYLELIBRE':
        return 'bg-red-100 text-red-700';
      case 'GARMIN':
        return 'bg-green-100 text-green-700';
      case 'APPLE':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-blue-100 text-blue-700';
    }
  };

  // State for active tab
  const [activeTab, setActiveTab] = React.useState<string>('general');
  
  // Create tabs for the modal
  const tabs: TabItem[] = [
    {
      id: 'general',
      label: 'General',
      icon: <i className="fas fa-info-circle mr-2"></i>,
      content: (
        <div className="prose max-w-none">
          <h3 className="text-lg font-medium text-gray-900 mt-0">Device Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-0">Basic Details</h4>
              </div>
              <div className="p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Provider:</span>
                  <span className={`text-sm py-1 px-3 rounded-full font-medium ${getProviderColorClasses(device.provider)}`}>
                    <i className={`fas ${getProviderIcon(device.provider)} mr-1`}></i>
                    {device.provider || 'Unknown'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Language:</span>
                  <span className="text-sm bg-gray-100 text-gray-700 py-1 px-2 rounded">
                    {device.language || '-'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Status:</span>
                  <span className={`text-sm py-1 px-2 rounded font-medium ${
                    device.enabled 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {device.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-0">System Information</h4>
              </div>
              <div className="p-4 space-y-4">
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 mb-1">Device ID</span>
                  <code className="text-sm bg-gray-50 p-2 rounded font-mono text-purple-700 break-all">
                    {device.id || '-'}
                  </code>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Created:</span>
                  <span className="text-sm text-gray-700">
                    {formatDate(device.createdAt)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Last Updated:</span>
                  <span className="text-sm text-gray-700">
                    {formatDate(device.updatedAt)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'parameters',
      label: 'Parameters',
      icon: <i className="fas fa-cog mr-2"></i>,
      content: (
        <div className="prose max-w-none">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-0 flex items-center">
                <i className="fas fa-sliders-h text-blue-500 mr-2"></i>
                Device Parameters
              </h4>
            </div>
            <div className="p-4">
              {device.parameters && Object.keys(device.parameters).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(device.parameters).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-start border-b border-gray-100 pb-3 last:border-b-0">
                      <span className="text-sm font-medium text-gray-600">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                      </span>
                      <span className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded ml-2 break-all">
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <i className="fas fa-inbox text-gray-300 text-5xl mb-3"></i>
                  <p className="text-gray-500 italic text-sm">No parameters configured for this device.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'user',
      label: 'User Info',
      icon: <i className="fas fa-user mr-2"></i>,
      content: (
        <div className="prose max-w-none">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-0 flex items-center">
                <i className="fas fa-user-circle text-green-500 mr-2"></i>
                Associated User
              </h4>
            </div>
            <div className="p-4">
              {device.user ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {device.user.username && (
                    <div>
                      <span className="text-xs font-medium text-gray-500">Username</span>
                      <p className="text-sm text-gray-900 mt-1">{device.user.username}</p>
                    </div>
                  )}
                  {device.user.email && (
                    <div>
                      <span className="text-xs font-medium text-gray-500">Email</span>
                      <p className="text-sm text-gray-900 mt-1">{device.user.email}</p>
                    </div>
                  )}
                  {device.user.first_name && (
                    <div>
                      <span className="text-xs font-medium text-gray-500">First Name</span>
                      <p className="text-sm text-gray-900 mt-1">{device.user.first_name}</p>
                    </div>
                  )}
                  {device.user.last_name && (
                    <div>
                      <span className="text-xs font-medium text-gray-500">Last Name</span>
                      <p className="text-sm text-gray-900 mt-1">{device.user.last_name}</p>
                    </div>
                  )}
                  {device.user.id && (
                    <div className="md:col-span-2">
                      <span className="text-xs font-medium text-gray-500">User ID</span>
                      <p className="text-sm text-gray-900 mt-1 font-mono bg-gray-50 p-2 rounded">{device.user.id}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-6">
                  <i className="fas fa-user-slash text-gray-300 text-5xl mb-3"></i>
                  <p className="text-gray-500 italic text-sm">No user information available.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'integration',
      label: 'Integration',
      icon: <i className="fas fa-plug mr-2"></i>,
      content: (
        <div className="prose max-w-none">
          <h3 className="text-lg font-medium text-gray-900 mt-0">Integration Details</h3>
          
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-0">Provider Specific Information</h4>
            </div>
            <div className="p-4 space-y-4">
              {device.provider === 'FREESTYLELIBRE' && (
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <i className="fas fa-heartbeat text-red-600 mr-2"></i>
                    <span className="font-medium text-red-900">FreeStyle Libre</span>
                  </div>
                  <p className="text-sm text-red-800">
                    Continuous glucose monitoring device integration.
                  </p>
                </div>
              )}
              
              {device.provider === 'GARMIN' && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <i className="fas fa-running text-green-600 mr-2"></i>
                    <span className="font-medium text-green-900">Garmin</span>
                  </div>
                  <p className="text-sm text-green-800">
                    Fitness and activity tracking device integration.
                  </p>
                </div>
              )}
              
              {device.provider === 'APPLE' && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <i className="fas fa-apple text-gray-600 mr-2"></i>
                    <span className="font-medium text-gray-900">Apple Health</span>
                  </div>
                  <p className="text-sm text-gray-800">
                    Apple Health Kit integration for health data synchronization.
                  </p>
                </div>
              )}
              
              <div className="border-t pt-4">
                <h5 className="text-sm font-medium text-gray-700 mb-2">Connection Status</h5>
                <div className="flex items-center">
                  <div className={`w-2 h-2 rounded-full mr-2 ${device.enabled ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className="text-sm text-gray-600">
                    Device is currently {device.enabled ? 'connected and active' : 'disconnected or inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <ModalPane 
      isOpen={isOpen} 
      onClose={onClose}
      title={
        <div className="flex items-center">
          <i className={`fas ${getProviderIcon(device.provider)} text-blue-500 mr-2`}></i>
          <span>Device Details</span>
          {device.provider && (
            <span className={`ml-3 text-xs py-1 px-2 rounded-full font-medium ${getProviderColorClasses(device.provider)}`}>
              {device.provider}
            </span>
          )}
        </div>
      }
      size="xl"
      footer={
        <Button 
          variant="secondary" 
          onClick={onClose}
          leftIcon={<i className="fas fa-times-circle mr-1"></i>}
        >
          Close
        </Button>
      }
    >
      <div className="py-3">
        {/* Tabs Navigation */}
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
        
        {/* Tab Content */}
        <div className="pt-4 mt-2">
          {tabs.find(tab => tab.id === activeTab)?.content}
        </div>
      </div>
    </ModalPane>
  );
};

export default DeviceDetailsView;